import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { lookupPlainBasePriceGbp } from "../../lib/pricing";
import { HOME_SELECT_BASE_EVENT, makePlainSeaMossLine } from "../../lib/plainGoldCart";
import { getProductPreview } from "../../lib/productPreviewImages";
import { useCart } from "../../context/CartContext";
import { HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";

/** Scroll / pinch zoom bounds for product preview (label readability). */
const ZOOM_MIN = 1;
const ZOOM_MAX = 4.5;
const ZOOM_WHEEL_STEP = 0.00135;

const BASES = [
  {
    id: "gold",
    name: "Gold",
    short: "Smooth, mild, everyday.",
  },
  {
    id: "purple",
    name: "Purple",
    short: "Deeper colour, bolder look.",
  },
];

/** Size options in ascending order — smallest to largest. */
const SIZES = [
  { sizeMl: 300, label: "300ml", badge: "Try it first" },
  { sizeMl: 500, label: "500ml", badge: "Most popular" },
  { sizeMl: 1000, label: "1L", badge: "Best value" },
];

/** Packaging in a fixed order — Jar first, then Pouch. */
const PACKS = [
  {
    id: "jar",
    name: "Jar",
    copy: "Glass jar — fridge-ready and easy to spoon from.",
  },
  {
    id: "pouch",
    name: "Pouch",
    copy: "Foil pouch — lighter to send, same fresh gel.",
  },
];

function readHashBase() {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  if (hash === "#choose-purple" || hash === "#purple") return "purple";
  return "gold";
}

function optionClass(selected, isPurple) {
  if (selected) {
    return isPurple
      ? "bg-plum/55 border-violet ring-2 ring-violet/60 text-ivory"
      : "bg-gold/25 border-gold ring-2 ring-gold/60 text-ivory";
  }
  return isPurple
    ? "bg-[rgba(16,26,19,0.85)] border-[rgba(248,241,223,0.14)] hover:border-violet/55 hover:bg-plum/30 hover:-translate-y-0.5"
    : "bg-[rgba(16,26,19,0.85)] border-[rgba(248,241,223,0.14)] hover:border-gold/55 hover:bg-gold/12 hover:-translate-y-0.5";
}

function clampZoom(n) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n));
}

/**
 * Cursor-anchored pan when changing scale (maps / Amazon style).
 * Origin is CSS transform-origin: 0 0 on the image layer.
 */
function panForZoomToward(prevZoom, nextZoom, panX, panY, cursorX, cursorY) {
  if (prevZoom <= 0) return { x: panX, y: panY };
  const scale = nextZoom / prevZoom;
  return {
    x: cursorX - (cursorX - panX) * scale,
    y: cursorY - (cursorY - panY) * scale,
  };
}

function ProductPreview({ base, packaging, sizeMl }) {
  const preview = getProductPreview(base, packaging, sizeMl);
  const isPurple = base === "purple";
  const isPouch = packaging === "pouch";
  const [missing, setMissing] = useState(false);
  const stageRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const [dragging, setDragging] = useState(false);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  const resetView = () => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setDragging(false);
    dragRef.current = null;
    pinchRef.current = null;
  };

  useEffect(() => {
    setMissing(false);
    resetView();
    // Reset whenever selection changes so zoom never sticks across Gold/Purple/size/pack
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.src, base, packaging, sizeMl]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  /* Non-passive wheel + pinch so we can block page scroll / browser zoom gestures */
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !preview.src || missing) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;
      const prev = zoomRef.current;
      const next = clampZoom(prev * Math.exp(-e.deltaY * ZOOM_WHEEL_STEP));
      if (Math.abs(next - prev) < 0.0005) return;
      const nextPan =
        next <= ZOOM_MIN + 0.001
          ? { x: 0, y: 0 }
          : panForZoomToward(prev, next, panRef.current.x, panRef.current.y, cursorX, cursorY);
      zoomRef.current = next;
      panRef.current = nextPan;
      setZoom(next);
      setPan(nextPan);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        dragRef.current = null;
        setDragging(false);
        const dist = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY,
        );
        pinchRef.current = { dist, zoom: zoomRef.current };
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || !pinchRef.current) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      const dist = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY,
      );
      const ratio = dist / Math.max(1, pinchRef.current.dist);
      const prev = zoomRef.current;
      const next = clampZoom(pinchRef.current.zoom * ratio);
      if (Math.abs(next - prev) < 0.0005) return;
      const nextPan =
        next <= ZOOM_MIN + 0.001
          ? { x: 0, y: 0 }
          : panForZoomToward(prev, next, panRef.current.x, panRef.current.y, midX, midY);
      zoomRef.current = next;
      panRef.current = nextPan;
      setZoom(next);
      setPan(nextPan);
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchRef.current = null;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [preview.src, missing]);

  if (!preview.src || missing) {
    return (
      <div
        className={`w-full h-full min-h-[300px] md:min-h-[460px] rounded-[1.5rem] border border-dashed flex flex-col items-center justify-center text-center px-6 ${
          isPurple ? "border-violet/40 bg-plum/20" : "border-gold/40 bg-gold/10"
        }`}
      >
        <span
          className={`inline-block w-3 h-3 rounded-full mb-4 ${
            isPurple ? "bg-plum shadow-[0_0_16px_rgba(139,95,191,0.6)]" : "bg-gold shadow-[0_0_16px_rgba(226,199,107,0.6)]"
          }`}
          aria-hidden
        />
        <p className="font-sans font-semibold text-xl text-ivory m-0 leading-tight">
          {preview.comingSoonLabel || "Product photo coming soon"}
        </p>
        <p className="mt-2 text-sm text-sage m-0">
          {isPurple ? "Purple" : "Gold"} · {isPouch ? "Pouch" : "Jar"} ·{" "}
          {sizeMl === 1000 ? "1L" : `${sizeMl}ml`}
        </p>
      </div>
    );
  }

  const zoomed = zoom > 1.02;
  const hint = isPouch
    ? "Scroll to zoom the label. Drag to pan when zoomed."
    : "Scroll to zoom. Drag to pan when zoomed.";

  return (
    <div
      ref={stageRef}
      className={`mm-product-preview mm-product-preview--scroll-zoom group relative w-full overflow-hidden rounded-[1.5rem] ring-1 min-h-[340px] sm:min-h-[400px] md:min-h-[520px] lg:min-h-[560px] ${
        isPurple ? "ring-violet/40 mm-product-preview--purple" : "ring-gold/45 mm-product-preview--gold"
      } ${zoomed ? "mm-product-preview--zoomed" : ""} ${dragging ? "mm-product-preview--dragging" : ""} ${
        hovering ? "mm-product-preview--hover" : ""
      }`}
      style={{
        background: isPurple
          ? "radial-gradient(ellipse 80% 70% at 50% 40%, #fffdf9 0%, #f6f0e8 52%, #e6dce8 100%)"
          : "radial-gradient(ellipse 80% 70% at 50% 40%, #fffdf8 0%, #f7f1e6 52%, #eadfc8 100%)",
        boxShadow: isPurple
          ? "0 0 48px -18px rgba(122,63,160,0.45), inset 0 1px 0 rgba(255,255,255,0.55)"
          : "0 0 48px -18px rgba(215,179,84,0.42), inset 0 1px 0 rgba(255,255,255,0.55)",
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => {
        setHovering(false);
        resetView();
      }}
      onDoubleClick={(e) => {
        e.preventDefault();
        resetView();
      }}
      onPointerDown={(e) => {
        if (e.pointerType === "touch") return;
        if (zoomRef.current <= 1.02) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX: panRef.current.x,
          panY: panRef.current.y,
        };
        setDragging(true);
      }}
      onPointerMove={(e) => {
        if (!dragRef.current || e.pointerType === "touch") return;
        const dx = e.clientX - dragRef.current.x;
        const dy = e.clientY - dragRef.current.y;
        const next = { x: dragRef.current.panX + dx, y: dragRef.current.panY + dy };
        panRef.current = next;
        setPan(next);
      }}
      onPointerUp={(e) => {
        if (e.pointerType === "touch") return;
        dragRef.current = null;
        setDragging(false);
        try {
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        } catch {
          /* already released */
        }
      }}
      onPointerCancel={() => {
        dragRef.current = null;
        setDragging(false);
        pinchRef.current = null;
      }}
      role="group"
      aria-label={`${preview.alt}. ${hint}`}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isPurple
            ? "radial-gradient(circle at 50% 60%, rgba(139,95,191,0.08), transparent 58%)"
            : "radial-gradient(circle at 50% 60%, rgba(201,164,73,0.1), transparent 58%)",
        }}
        aria-hidden
      />
      <div
        className="mm-product-preview__zoom-layer absolute inset-0"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "0 0",
        }}
      >
        <img
          src={preview.src}
          alt=""
          draggable={false}
          className="mm-product-preview__img absolute inset-0 w-full h-full object-contain p-3 sm:p-4 md:p-5 select-none"
          onError={() => setMissing(true)}
        />
      </div>
      <div
        className={`absolute bottom-0 inset-x-0 pointer-events-none px-4 pb-4 pt-12 bg-gradient-to-t from-[#2a241c]/28 via-[#2a241c]/08 to-transparent transition-opacity duration-200 ${
          zoomed ? "opacity-40" : "opacity-100"
        }`}
      >
        <p className="text-sm text-white font-medium m-0 drop-shadow-sm">
          {isPurple ? "Purple" : "Gold"} · {isPouch ? "Pouch" : "Jar"}
          <span className="hidden sm:inline text-white/85 font-normal">
            {" "}
            · {isPouch ? "scroll to zoom label" : "scroll to zoom"}
            {zoomed ? " · drag to pan · double-click reset" : ""}
          </span>
          <span className="sm:hidden text-white/85 font-normal"> · pinch to zoom</span>
        </p>
      </div>
      {hovering && !zoomed ? (
        <p
          className={`mm-product-preview__hint pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-[5] m-0 px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase font-semibold text-void/90 rounded-full backdrop-blur-sm ${
            isPurple ? "bg-violet/90" : "bg-gold/90"
          }`}
        >
          Scroll to zoom
        </p>
      ) : null}
      {zoomed ? (
        <button
          type="button"
          className="mm-product-preview__reset absolute top-3 right-3 z-[5] min-h-[32px] px-3 py-1.5 rounded-full text-[11px] tracking-[0.08em] uppercase font-semibold bg-void/75 text-ivory border border-white/20 hover:bg-void/90 transition"
          onClick={(e) => {
            e.stopPropagation();
            resetView();
          }}
        >
          Reset
        </button>
      ) : null}
    </div>
  );
}

export default function HomeOrderSelector({ headingLevel = "h2", className = "" }) {
  const { addItem, setOpen } = useCart();
  const [base, setBase] = useState(readHashBase);
  const [sizeMl, setSizeMl] = useState(500);
  const [packaging, setPackaging] = useState("jar");
  const HeadingTag = headingLevel === "h1" ? "h1" : "h2";

  useEffect(() => {
    const apply = (next) => {
      if (next === "gold" || next === "purple") setBase(next);
    };
    const onHash = () => apply(readHashBase());
    const onSelect = (event) => apply(event.detail?.base);
    window.addEventListener("hashchange", onHash);
    window.addEventListener(HOME_SELECT_BASE_EVENT, onSelect);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener(HOME_SELECT_BASE_EVENT, onSelect);
    };
  }, []);

  const pack = packaging === "pouch" ? "pouch" : "jar";
  const price = lookupPlainBasePriceGbp(base, sizeMl, pack);
  const sizeLabel = sizeMl === 1000 ? "1L" : `${sizeMl}ml`;
  const baseLabel = base === "purple" ? "Purple" : "Gold";
  const packLabel = pack === "pouch" ? "Foil Pouch" : "Glass Jar";
  const isPurple = base === "purple";
  const accentLabel = isPurple ? "text-violet" : "text-goldhover";
  const selectedPack = PACKS.find((p) => p.id === pack);

  const addToBasket = () => {
    const line = makePlainSeaMossLine({ base, sizeMl, packaging: pack });
    addItem(line);
    toast.success("Added to basket", {
      description: `${line.name} · ${pack === "pouch" ? "foil pouch" : "glass jar"}`,
    });
    setOpen(true);
  };

  return (
    <section
      id="choose-sea-moss"
      className={`relative py-9 md:py-12 scroll-mt-24 overflow-hidden bg-[color:var(--mm-bg-soft)] ${className}`.trim()}
      aria-labelledby="order-heading"
    >
      <span id="order" className="absolute top-0" aria-hidden />
      <span id="shop-favourites" className="absolute top-0" aria-hidden />
      <span id="choose-gold" className="absolute top-0" aria-hidden />
      <span id="choose-purple" className="absolute top-0" aria-hidden />

      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-500"
        style={{
          background: isPurple
            ? "radial-gradient(ellipse 50% 45% at 82% 8%, rgba(122,63,160,0.22), transparent 62%)"
            : "radial-gradient(ellipse 50% 45% at 18% 8%, rgba(215,179,84,0.18), transparent 62%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-5 md:mb-7">
          <HeadingTag
            id="order-heading"
            className="font-sans font-semibold text-3xl md:text-4xl text-ivory m-0 leading-tight tracking-tight"
          >
            Choose your sea moss
          </HeadingTag>
          <p className="mt-2 text-[15px] md:text-base text-sage leading-relaxed m-0">
            Start plain. Keep it simple. Gold or Purple, jar or pouch.
          </p>
        </div>

        <div className="home-glass-card mm-order-panel rounded-[1.85rem] p-5 sm:p-6 md:p-8">
          <div className="grid lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] gap-6 lg:gap-9 items-start">
            <div className="order-1">
              <ProductPreview base={base} packaging={pack} sizeMl={sizeMl} />
            </div>

            <div className="order-2 space-y-5">
              <div>
                <p className={`text-[11px] tracking-[0.2em] uppercase m-0 mb-2.5 ${accentLabel}`}>
                  Colour
                </p>
                <div className="grid grid-cols-2 gap-2.5" role="tablist" aria-label="Gold or Purple">
                  {BASES.map((item) => {
                    const selected = base === item.id;
                    const gold = item.id === "gold";
                    const tonePurple = item.id === "purple";
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setBase(item.id)}
                        className={`text-left rounded-[1.1rem] min-h-[96px] px-3.5 py-4 transition border ${optionClass(
                          selected,
                          tonePurple,
                        )}`}
                      >
                        <span className="flex items-center justify-between gap-2 mb-2">
                          <span
                            className={`inline-block w-3 h-3 rounded-full ${gold ? "bg-gold" : "bg-violet"}`}
                            aria-hidden
                          />
                          {selected ? (
                            <span
                              className={`text-[10px] tracking-[0.14em] uppercase font-semibold ${
                                gold ? "text-goldhover" : "text-violet"
                              }`}
                            >
                              Selected
                            </span>
                          ) : null}
                        </span>
                        <span className="block font-sans font-semibold text-xl text-ivory leading-tight">{item.name}</span>
                        <span className="block mt-1.5 text-sm text-sage leading-snug">{item.short}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className={`text-[11px] tracking-[0.2em] uppercase m-0 mb-2.5 ${accentLabel}`}>
                  Size
                </p>
                <div className="grid grid-cols-3 gap-2" role="group" aria-label="Size">
                  {SIZES.map((size) => {
                    const selected = sizeMl === size.sizeMl;
                    return (
                      <button
                        key={size.sizeMl}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setSizeMl(size.sizeMl)}
                        className={`rounded-[1rem] min-h-[78px] px-2 py-3 text-center transition border ${optionClass(
                          selected,
                          isPurple,
                        )}`}
                      >
                        <span className="block font-sans font-semibold text-lg text-ivory leading-tight">{size.label}</span>
                        <span className="block mt-1.5 text-[11px] tracking-wide uppercase text-sage">{size.badge}</span>
                        {selected ? (
                          <span className={`block mt-1.5 text-[10px] uppercase tracking-wider ${accentLabel}`}>Selected</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className={`text-[11px] tracking-[0.2em] uppercase m-0 mb-2.5 ${accentLabel}`}>
                  Jar or pouch
                </p>
                <div className="grid grid-cols-2 gap-2.5" role="group" aria-label="Packaging">
                  {PACKS.map((item) => {
                    const selected = pack === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setPackaging(item.id)}
                        className={`text-left rounded-[1.1rem] min-h-[78px] px-3.5 py-3.5 transition border ${optionClass(
                          selected,
                          isPurple,
                        )}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="block font-sans font-semibold text-lg text-ivory leading-tight">{item.name}</span>
                          {selected ? (
                            <span className={`text-[10px] tracking-[0.14em] uppercase font-semibold ${accentLabel}`}>
                              Selected
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedPack ? (
                  <p className="mt-2.5 text-[15px] text-sage leading-relaxed m-0">{selectedPack.copy}</p>
                ) : null}
              </div>

              <div
                className={`mm-order-summary rounded-[1.15rem] border px-4 py-5 sm:px-5 sm:py-5 ${
                  isPurple ? "border-violet/50 bg-plum/35" : "border-gold/50 bg-gold/18"
                }`}
                aria-live="polite"
              >
                <p className={`text-[11px] tracking-[0.2em] uppercase m-0 mb-2 ${accentLabel}`}>Selected</p>
                <p className="font-sans font-semibold text-xl sm:text-2xl text-ivory m-0 leading-snug">
                  {baseLabel} Sea Moss Gel
                </p>
                <p className="mt-1.5 text-base text-sage m-0 leading-snug">
                  {sizeLabel} · {packLabel}
                </p>
                <p
                  className={`mm-order-price mt-3.5 font-sans font-semibold tabular-nums tracking-tight m-0 leading-none ${
                    isPurple ? "text-[#c9a0e8]" : "text-goldhover"
                  }`}
                >
                  £{price.toFixed(2)}
                </p>
                <p className="mt-2.5 text-sm text-sage m-0">UK delivery calculated at checkout.</p>
              </div>

              <button
                type="button"
                onClick={addToBasket}
                data-testid={`add-${base}-${sizeMl}-${pack}`}
                className={
                  isPurple
                    ? "w-full min-h-[58px] px-8 py-4 inline-flex items-center justify-center gap-2 rounded-full bg-violet text-ivory font-semibold uppercase tracking-[0.1em] text-sm shadow-[0_12px_28px_-12px_rgba(122,63,160,0.55)] transition-all hover:bg-[#9456c0] hover:-translate-y-0.5 active:translate-y-0"
                    : `${HOME_CTA_DARK_PRIMARY} w-full min-h-[58px] px-8 py-4 text-sm`
                }
              >
                Add to Basket
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
